import type React from 'react';

import { useTreatLastNameInitialExperiment } from '#src/app/pages/petitionDetails/shared/hooks';

import { useRecentSignersConnection } from '../../hooks/useRecentSignersConnection';
import { MIN_SIGNERS_TO_SHOW } from '../../shared/constants';

type Props = Readonly<{
	/** `suppressTicker` from the signature-count experiment, which removes the carousel outright. */
	suppressed: boolean;
}>;

/**
 * Treats `sign_form_last_name_initial_experiment` and fires `$exposure` for every arm, once per opened
 * sign modal.
 *
 * Deliberately mounted **next to** `RecentSignersFeed` rather than inside it, and **not** behind
 * `signFormStep === 1`. Both placements matter, and they pull in opposite directions:
 *
 * - Inside the feed, the treat call sat above the `MIN_SIGNERS_TO_SHOW` early return, so sign-form views
 *   that rendered no carousel were still exposed — inflating the denominator the analysis divides by.
 *   Hence the qualification check here, which reapplies the feed's own two render conditions.
 * - Behind the step gate, a step 1 → 2 → 1 round-trip unmounts and remounts, and both `treatExperiment`
 *   and `$exposure` dedupe only per mount — so one visitor double-fired both. Sitting outside the gate,
 *   this mounts once per opened modal. Same reasoning as the `TrackExperimentExposure` sibling alongside
 *   it, whose comment records the identical decision.
 *
 * Renders nothing. Mount exactly once per modal, from a location that mounts in every arm — control and
 * fake_control render full names but must be exposed on the same terms as variant_1.
 */
export const RecentSignersFeedTracking: React.FC<Props> = ({ suppressed }) => {
	const recentSigners = useRecentSignersConnection();
	const carouselRendered = !suppressed && recentSigners.length >= MIN_SIGNERS_TO_SHOW;

	useTreatLastNameInitialExperiment(carouselRendered);

	return null;
};
